const { logLevels, stringify } = require('../helpers');
const Sentry = require('@sentry/browser');
/**
 * @typedef {import('../helpers')} LogLevel
 */

/**
 *
 * @param {object[]} message(s)
 * @param {object} [userContext] Required when environment variable LOG_CONTEXT_INFO = true
 * @param {string} [userContext.tenantId]
 * @param {object} [userContext.activeTenant]
 * @param {string} [userContext.appId]
 * @param {object} [userContext.appContext]
 * @param {string} [userContext.userId]
 * @param {string} [userContext.userName]
 * @param {LogLevel} logLevel Level to log message with
 */
function output(message, userContext, logLevel) {
    if (message[0] instanceof Error) {
        message[0] = message[0].stack;
    }
    if (message.length === 1) {
        message = message[0];
    } else {
        message = stringify([message]);
    }

    const needStringify = typeof message === 'object';
    const messageOut = needStringify ? JSON.stringify(message) : message;
    switch (logLevel) {
        case logLevels.ERROR:
            Sentry.captureException(messageOut);
            break;
        case logLevels.WARN:
            Sentry.captureMessage(messageOut, 'warning');
            break;
        case logLevels.INFO:
            Sentry.captureMessage(messageOut, 'info');
            break;
        case logLevels.DEBUG:
            Sentry.captureMessage(messageOut, 'debug');
            break;
    }
}

/**
 * Use parameters from the logger to set up sentry.
 * In case the app does not have everything at the beginning, monkey-patch
 * the setters to intercept changes and update Sentry.
 * @param {any} logger index.js for this module
 */
function init(logger) {
    const { release, environment, userId, tenantId, useCaseTitle, sampleRate = 1, ignoredErrors } = logger;
    Sentry.init({
        release,
        environment,
        dsn: 'https://cb7321c57c084fe591afdc88a6df4c8e@sentry.io/2831296',
        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
        transportOptions: {
            /**
             * Maximum number of events to store
             * Default: 30
             */
            maxQueueSize: 30
        },
        // percentage of errors to send to sentry
        sampleRate,
        // filter out "errors" that are actually user errors
        beforeSend,
        beforeBreadcrumb: filterBreadCrumbs(ignoredErrors),
        ignoreErrors: ignoredErrors
    });
    if (userId) {
        Sentry.setUser({ id: userId });
    }

    if (tenantId) {
        Sentry.setTag('tenantId', tenantId);
    }
    if (useCaseTitle) {
        Sentry.setTag('useCaseTitle', useCaseTitle);
    }

    // Monkey patch the setters.
    const tempUserId = logger.setUserId.bind(logger);
    logger.setUserId = function (userId) {
        tempUserId(userId);
        Sentry.setUser({ id: userId });
    };
    const tempTenantId = logger.setTenantId.bind(logger);
    logger.setTenantId = function (tenantId) {
        tempTenantId(tenantId);
        Sentry.setTag('tenantId', tenantId);
    };

    const tempUseCase = logger.setUseCaseTitle.bind(logger);
    logger.setUseCaseTitle = function (useCaseTitle) {
        tempUseCase(useCaseTitle);
        Sentry.setTag('useCaseTitle', useCaseTitle);
    };
}

module.exports = {
    output,
    init
};

function beforeSend(event, hint) {
    const error = hint.originalException;
    if (error && error._isUserError) {
        return null;
    }
    return event;
}

function filterBreadCrumbs(ignoredErrors) {
    return function (crumb) {
        // these are exceptions logged as errors. We don't need the sentry event
        if (crumb.category === 'sentry' && ignoredErrors.includes(crumb.message)) {
            return null;
        }

        return crumb;
    };
}
